import React, { BaseSyntheticEvent, SyntheticEvent } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { MechanicType } from 'shared/types';
import { useGetActivityTypesQuery } from 'features/getActivityTypes/api/getActivityTypesApi';
import { useGetMechanicsQuery } from 'features/getMechanics/api/getMechanicsApi';
import { PromoStateType } from 'entities/promo/types';
import { useCacheData } from 'shared/hooks';
import { useGetLanguagesQuery } from 'features/getLanguages/getLanguagesApi';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';
import { UploadImage } from 'shared/ui/UploadComponents/ui/UploadImage';
import { useUploadActivityImageMutation } from 'features/createActivity/api/createActivityApi';
import { toast } from 'react-toastify';

type CardPromoType = {
  state: PromoStateType;
  setState: React.Dispatch<React.SetStateAction<PromoStateType>>;
  form: Partial<UseFormReturn<any>>;
  onSubmit: (e?: BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>;
  textButton: string;
};

type TabPanelTypeProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

const PromoCard = ({ state, setState, form, onSubmit, textButton }: CardPromoType) => {
  const { data: activityTypesList } = useGetActivityTypesQuery(null);
  const { data: mechanics } = useGetMechanicsQuery({ isActive: true });
  const { data: languagesList, isSuccess: languagesListSuccess } = useGetLanguagesQuery(null);
  const [uploadActivityImage, { isLoading: isUploadingImage }] = useUploadActivityImageMutation();

  const { control, setValue, formState: { errors = {} } = {} } = form;

  const handleImageUpload = async (file: File | null) => {
    if (!file) {
      setValue?.('image', null);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await uploadActivityImage(formData).unwrap();

      if (response.file_path) {
        setValue?.('image', response.file_path);
        toast.success('Изображение успешно загружено', {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch (error) {
      toast.error('Ошибка при загрузке изображения', {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  const TabPanel = (props: TabPanelTypeProps) => {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ marginTop: '16px' }}>
            <Typography>{children}</Typography>
          </Box>
        )}
      </div>
    );
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setState({ ...state, value: newValue });
  };

  const handleActivityChange = (date: any) => {
    const findActivity = activityTypesList?.items.find((activity) => activity.id === date);
    setValue?.('activity_type_id', findActivity?.id);
  };

  const cachedMechanics = useCacheData<MechanicType>(mechanics?.items as MechanicType[]);
  const isButtonDisabled = Object.keys(errors).length > 0;

  const handleDateChange = (value: Dayjs | null, onChange: (...event: any[]) => void) => {
    onChange(value ? dayjs(value).format('YYYY-MM-DDTHH:mm') : null);
  };

  return (
    <>
      {languagesListSuccess && (
        <div>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={state.value}
                onChange={handleChange}
                aria-label="basic tabs example"
              >
                {languagesList.map((language, index) => (
                  <Tab
                    key={language.codename}
                    label={language.codename}
                    {...a11yProps(index)}
                  />
                ))}
              </Tabs>
            </Box>
            {languagesList.map((language, index) => (
              <TabPanel
                key={language.codename}
                value={state.value}
                index={index}
              >
                <Controller
                  control={control}
                  name={`name_${language.codename}`}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Название акции"
                      size="small"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        marginBottom: '24px',
                      }}
                      value={field.value || ''}
                      onChange={field.onChange}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`description_${language.codename}`}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={(e) => field.onChange(e)}
                      label="Описание акции"
                      size="small"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        marginBottom: '24px',
                      }}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`title_before_${language.codename}`}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={(e) => field.onChange(e)}
                      label="Заголовок до участия"
                      size="small"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        marginBottom: '24px',
                      }}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`description_before_${language.codename}`}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={(e) => field.onChange(e)}
                      label="Описание до участия"
                      size="small"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        marginBottom: '24px',
                      }}
                    />
                  )}
                />
              </TabPanel>
            ))}
          </Box>
        </div>
      )}

      <Box sx={{ width: '100%', marginTop: '24px' }}>
        <Typography mb={2}>Изображение акции</Typography>
        <Controller
          control={control}
          name="image"
          render={({ field: { value, onChange } }) => (
            <UploadImage
              value={value}
              onChange={handleImageUpload}
              textLabel="Изображение"
              helperText="Загрузите изображение для акции"
              textButton="Загрузить изображение"
              errors={errors?.image}
            />
          )}
        />
      </Box>

      <Box sx={{ width: '100%' }}>
        <Typography mb={3}>Настройки условий</Typography>

        <Controller
          name="activity_type_id"
          control={control}
          render={({ field: { value, onChange, ...restField } }) => (
            <FormControl fullWidth>
              <InputLabel id="select-label-type">Тип акции</InputLabel>
              <Select
                labelId="select-label-type"
                label="Тип акции"
                value={value ?? ''}
                onChange={(event) => handleActivityChange(event.target.value)}
                {...restField}
              >
                {activityTypesList?.items.map((item) => (
                  <MenuItem
                    key={item.id}
                    value={item.id}
                  >
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
        {mechanics && (
          <Controller
            control={control}
            name="objective_ids"
            rules={{ required: 'Поле обязательно для заполнения' }}
            render={({ field: { value, ...restField } }) => (
              <FormControl
                fullWidth
                sx={{ mt: 3 }}
              >
                <InputLabel id="checkbox-label-objectives">Механики акции*</InputLabel>
                <Select
                  labelId="checkbox-label-objectives"
                  multiple
                  value={value}
                  label="Механики акции"
                  renderValue={(selected) => selected.map((item: number) => cachedMechanics[item].name).join(', ')}
                  {...restField}
                >
                  {mechanics.items.map((mechanic: MechanicType) => (
                    <MenuItem
                      key={mechanic.id}
                      value={mechanic.id}
                    >
                      <Checkbox checked={value?.includes(mechanic.id)} />
                      <ListItemText primary={mechanic.name} />
                    </MenuItem>
                  ))}
                </Select>
                {errors?.objective_ids && (
                  <FormHelperText error>{errors.objective_ids.message?.toString()}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        )}
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale="en-gb"
        >
          <Typography
            mt={3}
            mb={3}
          >
            Период регистрации
          </Typography>

          <Box sx={{ display: 'flex', marginBottom: '5px', gap: '30px' }}>
            <Controller
              control={control}
              name="reg_start_date"
              defaultValue=""
              render={({ field: { value, onChange, ...restField } }) => (
                <DateTimePicker
                  label="Дата начала"
                  value={value ? dayjs(value) : null}
                  onChange={(value) => handleDateChange(value, onChange)}
                  {...restField}
                />
              )}
            />

            <Controller
              control={control}
              name="reg_end_date"
              defaultValue=""
              render={({ field: { value, onChange, ...restField } }) => (
                <DateTimePicker
                  label="Дата окончания"
                  value={value ? dayjs(value) : null}
                  onChange={(value) => handleDateChange(value, onChange)}
                  {...restField}
                />
              )}
            />
          </Box>
          <Typography
            mt={3}
            mb={3}
          >
            Период активности
          </Typography>
          <Box sx={{ display: 'flex', marginBottom: '5px', gap: '30px' }}>
            <Controller
              control={control}
              name="activity_start_date"
              defaultValue=""
              render={({ field: { value, onChange, ...restField } }) => (
                <DateTimePicker
                  label="Дата начала"
                  value={value ? dayjs(value) : null}
                  onChange={(value) => handleDateChange(value, onChange)}
                  {...restField}
                />
              )}
            />
            <Controller
              control={control}
              name="activity_end_date"
              defaultValue=""
              render={({ field: { value, onChange, ...restField } }) => (
                <DateTimePicker
                  label="Дата окончания"
                  value={value ? dayjs(value) : null}
                  onChange={(value) => handleDateChange(value, onChange)}
                  {...restField}
                />
              )}
            />
          </Box>
          <Typography
            mt={3}
            mb={3}
          >
            Период доступа бонусов
          </Typography>
          <Box sx={{ display: 'flex', marginBottom: '5px', gap: '30px' }}>
            <Controller
              control={control}
              name="bonus_start_date"
              defaultValue=""
              render={({ field: { value, onChange, ...restField } }) => (
                <DateTimePicker
                  label="Дата начала"
                  value={value ? dayjs(value) : null}
                  onChange={(value) => handleDateChange(value, onChange)}
                  {...restField}
                />
              )}
            />
            <Controller
              control={control}
              name="bonus_end_date"
              defaultValue=""
              render={({ field: { value, onChange, ...restField } }) => (
                <DateTimePicker
                  label="Дата окончания"
                  value={value ? dayjs(value) : null}
                  onChange={(value) => handleDateChange(value, onChange)}
                  {...restField}
                />
              )}
            />
          </Box>
        </LocalizationProvider>
      </Box>
      <Box
        sx={{
          display: 'flex',
          marginTop: '30px',
        }}
      >
        <Button
          onClick={onSubmit}
          variant="contained"
          sx={{ textTransform: 'none' }}
          disabled={isButtonDisabled}
        >
          {textButton}
        </Button>
      </Box>
    </>
  );
};

export default PromoCard;
